import { onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue";
import type { DocumentPictureInPictureOptions, UseDocumentPipOptions } from "@/types/document-pip";
import { useSnackbarStore } from "@/store/snackbar.store";

export function useDocumentPip(options: UseDocumentPipOptions = {}) {
  const { width = 1200, height = 800, onClose } = options;

  const { showSnackbarError } = useSnackbarStore();

  const isSupported = "documentPictureInPicture" in window;

  const pipWindow = shallowRef<Window | null>(null);
  const pipContainer = shallowRef<HTMLElement | null>(null);
  const isActive = ref(false);

  function copyStyles(target: Window): void {
    for (const styleSheet of Array.from(document.styleSheets)) {
      try {
        const cssText = Array.from(styleSheet.cssRules)
          .map((rule) => rule.cssText)
          .join("");
        const style = target.document.createElement("style");
        style.textContent = cssText;
        target.document.head.appendChild(style);
      } catch {
        if (!styleSheet.href) {
          continue;
        }
        const link = target.document.createElement("link");
        link.rel = "stylesheet";
        link.type = styleSheet.type;
        link.media = styleSheet.media.mediaText;
        link.href = styleSheet.href;
        target.document.head.appendChild(link);
      }
    }
  }

  async function open(overrideOptions?: DocumentPictureInPictureOptions): Promise<void> {
    if (!isSupported || !window.documentPictureInPicture) {
      return;
    }

    const existingWindow = window.documentPictureInPicture.window;

    if (existingWindow) {
      existingWindow.focus();

      if (existingWindow !== pipWindow.value) {
        onClose?.();
      }

      return;
    }

    try {
      const win = await window.documentPictureInPicture.requestWindow(overrideOptions ?? { width, height });

      copyStyles(win);
      win.document.body.style.margin = "0";

      const wrap = win.document.createElement("div");
      win.document.body.appendChild(wrap);

      // listen to PIP closing and reset all reactive data
      win.addEventListener("pagehide", close, { once: true });

      pipWindow.value = win;
      pipContainer.value = wrap;
      isActive.value = true;
    } catch {
      close();
    }
  }

  function close(): void {
    pipContainer.value = null;
    isActive.value = false;
    const win = pipWindow.value;
    pipWindow.value = null;
    win?.close();
  }

  watch(isActive, (active) => {
    if (!active) {
      onClose?.();
    }
  });

  onMounted(async () => {
    if (!isSupported) {
      showSnackbarError("Picture-in-Picture is not supported in this browser");

      return;
    }

    await open();
  });

  onBeforeUnmount(() => {
    close();
  });

  return { isSupported, isActive, pipWindow, pipContainer, open, close };
}
